import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    Paper,
    useTheme,
    useMediaQuery,
    Container
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react'; // Added useMemo

import VehicleExploreSection from '../components/VehicleExploreSection';
import CommercialArticlesSection, { ArticleData } from '../components/CommercialArticlesSection';
import axiosInstance from '../services/axiosInstance';
import Footer from '../components/Footer';
import Header from '../components/Header';

// Icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';

import { encodeId } from '../utils/idEncoder';

const ArticlePage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Data State
    const [allArticles, setAllArticles] = useState<ArticleData[]>([]); // Store ALL fetched articles

    // Search State
    const [searchText, setSearchText] = useState('');
    const [filterQuery, setFilterQuery] = useState(''); // Actual query used for filtering

    // --- 1. Fetch Ads AND Articles ---
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    { articleData: true },
                    { headers: { "Content-Type": "application/json" } }
                );
                const result = response.data as any;

                if (result.responseCode === "00" && result.responseData) {
                    setAllArticles(result.responseData.articleData || []);
                }
            } catch (error) {
                console.log("❌ Error fetching data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // --- 2. Fetch Vehicles ---
    useEffect(() => {
        const fetchVehicles = async () => {
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleGetListPublished",
                    {},
                    { headers: { "Content-Type": "application/json" } }
                );
                const result = response.data as any;

                if (result.responseCode === "00" && result.responseData) {
                    setVehicles(result.responseData.vehicleList || []);
                }
            } catch {
                console.log("❌ Error fetching vehicles");
            }
        };
        fetchVehicles();
    }, []);

    // --- Filter Logic ---
    // Memoize the filtering so it doesn't run on every render, only when query or list changes
    const filteredArticles = useMemo(() => {
        if (!filterQuery) return allArticles;

        const lowerQuery = filterQuery.toLowerCase();

        return allArticles.filter((article) => {
            const titleMatch = article.articleTitle?.toLowerCase().includes(lowerQuery);
            const descMatch = article.articleDescription?.toLowerCase().includes(lowerQuery);

            return titleMatch || descMatch;
        });
    }, [allArticles, filterQuery]);

    // --- Search Handler ---
    const handleSearch = () => {
        // Update the filter query state which triggers the re-calculation of filteredArticles
        setFilterQuery(searchText);
    };

    // Optional: Auto-search as user types (Debouncing recommended for API calls, but fine for client-side filtering)
    // useEffect(() => {
    //     setFilterQuery(searchText);
    // }, [searchText]);

    return (
        <>
            <Header />

            {/* --- HERO / SEARCH SECTION --- */}
            <Box
                sx={{
                    pt: { xs: 12, md: 16 },
                    pb: { xs: 6, md: 8 },
                    background: 'linear-gradient(180deg, #F4F6F8 0%, #FFFFFF 100%)',
                    textAlign: 'center',
                    px: 2
                }}
            >
                <Container maxWidth="lg">
                    <Grid container justifyContent="center">
                        <Grid size={{ xs: 12, md: 10 }}>
                            {/* HEADLINE */}
                            <Typography
                                variant={isMobile ? "h5" : "h4"}
                                component="h1"
                                sx={{
                                    fontWeight: 550,
                                    color: '#1A2027',
                                    lineHeight: 1.1,
                                    textShadow: '0px 4px 20px rgba(0,0,0,0.05)',
                                    mb: 2
                                }}
                            >
                                Expert Insights on the Commercial Vehicle Industry
                            </Typography>

                            {/* SUBTITLE */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    maxWidth: '600px',
                                    mx: 'auto',
                                    fontSize: { xs: '1rem', md: '1rem' },
                                    fontWeight: 500,
                                    fontFamily: 'Inter, sans-serif',
                                    mb: 2
                                }}
                            >
                                Stay up-to-date with the latest market trends, buying tips, and industry news for commercial vehicles.
                            </Typography>
                        </Grid>

                        {/* SEARCH BAR */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Paper
                                elevation={4}
                                sx={{
                                    p: '6px',
                                    pl: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '50px',
                                    width: '100%',
                                    maxWidth: '750px',
                                    mx: 'auto',
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    transition: 'box-shadow 0.3s ease',
                                    '&:hover': {
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                                    }
                                }}
                            >
                                {/* Input Field */}
                                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 1 }}>
                                    <SearchIcon sx={{ color: 'text.disabled', mr: 1 }} />
                                    <TextField
                                        fullWidth
                                        placeholder="Search articles..."
                                        variant="standard"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: { fontSize: { xs: '0.95rem', md: '1rem' }, color: '#444' }
                                        }}
                                    />
                                </Box>

                                {/* Action Button */}
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    endIcon={<ArrowForwardIcon fontSize="small" />}
                                    sx={{
                                        borderRadius: '50px',
                                        minWidth: { xs: '120px', md: '160px' },
                                        height: { xs: '45px', md: '50px' },
                                        background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
                                        color: '#fff',
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                        fontWeight: 700,
                                        boxShadow: '0 4px 14px 0 rgba(255, 142, 0, 0.39)',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #FF8E00 0%, #FFA100 100%)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 6px 20px rgba(255, 142, 0, 0.23)'
                                        }
                                    }}
                                >
                                    Browse Articles
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* --- ARTICLE LIST SECTION --- */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 1200,
                    mx: 'auto',
                    mt: 4,
                    mb: 8,
                    position: 'relative',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column', // Added column direction for potential "no results" message
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: 2
                }}
            >
                {/* Optional: Show message if search yields no results */}
                {filterQuery && filteredArticles.length === 0 && !loading && (
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                        No articles found for "{filterQuery}"
                    </Typography>
                )}

                <CommercialArticlesSection
                    articles={filteredArticles} // PASS FILTERED ARTICLES HERE
                    isLoading={loading}
                />
            </Box>

            {/* --- EXPLORE VEHICLES SECTION --- */}
            <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', pb: 8, px: 2 }}>
                {vehicles?.length > 0 && (
                    <VehicleExploreSection
                        title="Explore Our Vehicles"
                        navigateTo="/vehicle-listing"
                        vehicle={vehicles}
                    />
                )}
            </Box>

            <Footer />
        </>
    );
};

export default ArticlePage;